import React, { useState, useEffect, useRef } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Search, Eye, Edit, Trash2, Calendar, AlertTriangle, RotateCcw, FileText, Upload, X, ToggleLeft, ToggleRight, CheckSquare, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import { format, parseISO, isValid } from 'date-fns';

const STATUSES = ['all', 'active', 'inactive', 'completed', 'on_hold', 'cancelled', 'deleted'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const safeDate = (d) => {
  try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM yyyy') : '-'; } catch { return '-'; }
};

// Helper to format date for input[type="date"] (YYYY-MM-DD)
// Handles UTC dates correctly by extracting ISO date part
const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  try {
    // If it's an ISO string like "2024-03-15T00:00:00Z", extract just the date part
    if (dateStr.includes('T')) {
      return dateStr.split('T')[0]; // Returns "2024-03-15"
    }
    // Fallback: parse as date
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return '';
  }
};

const emptyForm = { name: '', client_name: '', client_phone: '', description: '', priority: 'medium', order_date: '', deadline: '', total_amount: '', notes: '', is_ready: false };

// ── MO References Section (Images + PDFs) ────────────────────────────────────
function MoReferencesSection({ projectId, refreshKey, onUpload, onDelete, uploading }) {
  const [files, setFiles] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    api.get(`/projects/${projectId}/mo-references`)
      .then(r => setFiles(r.data))
      .catch(() => {});
  }, [projectId, refreshKey]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length) onUpload(selectedFiles);
    e.target.value = '';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500">{files.length} file(s)</span>
        <button type="button" onClick={() => fileRef.current.click()} disabled={uploading}
          className="flex items-center gap-1 text-xs bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg font-medium disabled:opacity-50">
          <Upload className="w-3 h-3" /> {uploading ? 'Upload...' : 'Upload'}
        </button>
        <input ref={fileRef} type="file" accept="image/*,.pdf" multiple className="hidden" onChange={handleFileChange} />
      </div>
      {files.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center text-xs text-gray-400">
          No MO references — Upload images or PDFs
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {files.map(file => (
            <div key={file.id} className="relative group">
              {file.resource_type === 'raw' ? (
                // PDF Display
                <div className="w-full h-24 rounded-xl border border-gray-200 bg-red-50 flex items-center justify-center cursor-pointer group-hover:ring-2 group-hover:ring-amber-400 transition-all"
                  onClick={() => window.open(file.image_url, '_blank')}>
                  <FileText className="w-8 h-8 text-red-500" />
                </div>
              ) : (
                // Image Display
                <img src={file.image_url} alt="mo-ref"
                  onClick={() => setLightbox(file)}
                  className="w-full h-24 object-cover rounded-xl border border-gray-200 cursor-pointer group-hover:ring-2 group-hover:ring-amber-400 transition-all"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              )}
              <button type="button" onClick={() => onDelete(file.id)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {lightbox && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <img src={lightbox.image_url} alt="full" className="w-full rounded-2xl shadow-2xl max-h-[80vh] object-contain" />
            <button onClick={() => setLightbox(null)} className="absolute top-3 right-3 bg-white/20 hover:bg-white/40 text-white rounded-full p-1.5">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Searchable client dropdown (free-form + autocomplete) ────────────────────
function ClientSelect({ clients, value, onChange, onClientSelect, placeholder }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen]   = useState(false);
  const ref = useRef();
  useEffect(() => { setQuery(value || ''); }, [value]);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const filtered = clients.filter(c =>
    !query || (c.name||'').toLowerCase().includes(query.toLowerCase()) || (c.phone||'').includes(query)
  ).slice(0, 10);
  return (
    <div ref={ref} className="relative">
      <input
        className="field-input"
        placeholder={placeholder}
        value={query}
        onFocus={() => setOpen(true)}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-[200] w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-48 overflow-y-auto">
          <div className="p-1">
            {filtered.map((c, i) => (
              <button key={i} type="button"
                className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-blue-50 transition-colors"
                onMouseDown={e => { e.preventDefault(); onClientSelect(c); setQuery(c.name); setOpen(false); }}
              >
                <span className="font-semibold text-gray-800">{c.name}</span>
                {c.phone && <span className="text-xs text-gray-400 ml-2">{c.phone}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProjectsPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('projects');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countsLoading, setCountsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [counts, setCounts] = useState({});
  const [readyFilter, setReadyFilter] = useState('all');
  const [sortBy, setSortBy] = useState('order_date_desc'); // default: latest order date first
  const [showModal, setShowModal] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [moReferenceFiles, setMoReferenceFiles] = useState([]);
  const [moRefUploading, setMoRefUploading] = useState(false);
  const [moRefRefresh, setMoRefRefresh] = useState(0);
  const [clients, setClients]         = useState([]);
  const [filterClient, setFilterClient] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  // Detect if rendered inside worker layout (/worker/...) or admin layout
  const basePath = location.pathname.startsWith('/worker') ? '/worker' : '/admin';
  const { formatDate, formatCurrency } = useSettings();

  // Client-side sort — nulls always go to the bottom
  const sortedProjects = [...projects].sort((a, b) => {
    const toMs = (d) => (d ? new Date(d).getTime() : null);
    const sortField = (aVal, bVal, asc) => {
      const [av, bv] = [toMs(aVal), toMs(bVal)];
      if (av === null && bv === null) return 0;
      if (av === null) return 1;   // nulls sink to bottom
      if (bv === null) return -1;
      return asc ? av - bv : bv - av;
    };
    if (sortBy === 'order_date_asc')  return sortField(a.order_date, b.order_date, true);
    if (sortBy === 'order_date_desc') return sortField(a.order_date, b.order_date, false);
    if (sortBy === 'deadline_asc')   return sortField(a.deadline,   b.deadline,   true);
    if (sortBy === 'deadline_desc')  return sortField(a.deadline,   b.deadline,   false);
    return 0;
  });

  // Filter by selected client
  const displayedProjects = filterClient
    ? sortedProjects.filter(p => (p.client_name||'').toLowerCase() === filterClient.toLowerCase())
    : sortedProjects;

  // Toggle sort: first click = newest first (desc), second = oldest first (asc), third = off
  const toggleSort = (field) => {
    if (sortBy === `${field}_desc`) { setSortBy(`${field}_asc`); return; }
    if (sortBy === `${field}_asc`)  { setSortBy(''); return; }
    setSortBy(`${field}_desc`);
  };

  const sortIcon = (field) => {
    if (sortBy === `${field}_asc`)  return <ArrowUp   className="w-3 h-3 inline ml-0.5 text-amber-500" />;
    if (sortBy === `${field}_desc`) return <ArrowDown className="w-3 h-3 inline ml-0.5 text-amber-500" />;
    return <ArrowUpDown className="w-3 h-3 inline ml-0.5 opacity-30" />;
  };

  const load = async () => {
    setLoading(true);
    const params = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    if (search) params.search = search;
    if (readyFilter !== 'all') params.ready = readyFilter;
    const res = await api.get('/projects', { params });
    setProjects(res.data);
    setLoading(false);
  };

  // Load counts for all statuses — respects client filter
  const loadCounts = async () => {
    setCountsLoading(true);
    try {
      const res = await api.get('/projects', { params: { lite: 1 } });
      const all = filterClient
        ? res.data.filter(p => (p.client_name || '').toLowerCase() === filterClient.toLowerCase())
        : res.data;
      const c = { all: all.length };
      ['active','inactive','completed','on_hold','cancelled','deleted'].forEach(s => {
        c[s] = all.filter(p => p.status === s).length;
      });
      c['not_ready'] = all.filter(p => !p.is_ready).length;
      setCounts(c);
    } catch {}
    finally { setCountsLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, search, readyFilter]);
  useEffect(() => { loadCounts(); }, [filterClient]);
  useEffect(() => { loadCounts(); }, []);
  useEffect(() => { api.get('/admin/clients-list').then(r => setClients(r.data)).catch(() => {}); }, []);

  const openCreate = () => { setEditProject(null); setForm(emptyForm); setMoReferenceFiles([]); setShowModal(true); };
  const openEdit = (p, e) => {
    e.stopPropagation();
    setEditProject(p);
    setForm({ 
      name: p.name, 
      client_name: p.client_name||'', 
      client_phone: p.client_phone||'', 
      description: p.description||'', 
      priority: p.priority, 
      order_date: formatDateForInput(p.order_date),
      deadline: formatDateForInput(p.deadline), 
      total_amount: p.total_amount||'', 
      notes: p.notes||'', 
      status: p.status,
      is_ready: !!p.is_ready
    });
    setMoReferenceFiles([]);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editProject) {
        await api.put(`/projects/${editProject.id}`, form);
        toast.success('Project updated');
        setShowModal(false);
        setMoReferenceFiles([]);
        load();
        return;
      }

      // New project creation — images pehle temp-upload karo, phir project create
      let imageUrls = [];
      if (moReferenceFiles.length > 0) {
        try {
          toast.loading('Uploading images...', { id: 'img-upload' });
          const formData = new FormData();
          moReferenceFiles.forEach(file => formData.append('images', file));
          const uploadRes = await api.post('/temp-upload/images', formData, {
            timeout: 60000,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          imageUrls = uploadRes.data.images || [];
          toast.dismiss('img-upload');
        } catch (uploadErr) {
          toast.dismiss('img-upload');
          toast.error('Image upload failed: ' + (uploadErr.response?.data?.message || uploadErr.message));
          return;
        }
      }

      const payload = imageUrls.length > 0 ? { ...form, image_urls: imageUrls } : form;
      const response = await api.post('/projects', payload);

      // 202 = approval pending (worker ne request ki, admin approve karega)
      if (response.status === 202 || response.data?.approval_status === 'PENDING') {
        toast.success(`✅ Request submitted! Admin approval ke baad project banega. (ID: #${response.data.approval_id})`);
        setShowModal(false);
        setMoReferenceFiles([]);
        return;
      }

      const projectId = response.data.id;
      toast.success('Project created successfully ✅');
      setShowModal(false);
      setMoReferenceFiles([]);
      load();
    } catch (err) {
      console.error('Project save error:', err);
      toast.error(err.response?.data?.message || 'Error saving project');
    }
  };

  const uploadMoReferences = async (projectId) => {
    if (!moReferenceFiles.length) {
      console.log('📤 No MO reference files to upload');
      return;
    }

    setMoRefUploading(true);
    try {
      console.log(`📤 Uploading ${moReferenceFiles.length} MO reference file(s) for project ${projectId}`);
      const formData = new FormData();
      moReferenceFiles.forEach((file, idx) => {
        console.log(`  [${idx + 1}] ${file.name} (${file.size} bytes, type: ${file.type})`);
        formData.append('images', file);
      });

      const response = await api.post(`/projects/${projectId}/mo-references`, formData);
      console.log('✅ MO references upload successful:', response.data);
      toast.success(`${moReferenceFiles.length} file(s) uploaded`);
      setMoReferenceFiles([]);
      setMoRefRefresh(prev => prev + 1); // Trigger refresh in component
    } catch (err) {
      console.error('❌ MO reference upload failed:', err);
      console.error('  Error details:', err.response?.data || err.message);
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setMoRefUploading(false);
    }
  };

  const handleMoRefUpload = async (files, projectId) => {
    if (!files.length) return;
    setMoReferenceFiles(files); // Store for upload
    // Auto-upload immediately
    setMoRefUploading(true);
    try {
      console.log(`📤 Uploading ${files.length} MO reference file(s) for project ${projectId}`);
      const formData = new FormData();
      files.forEach((file, idx) => {
        console.log(`  [${idx + 1}] ${file.name} (${file.size} bytes, type: ${file.type})`);
        formData.append('images', file);
      });

      const response = await api.post(`/projects/${projectId}/mo-references`, formData);
      console.log('✅ MO references upload successful:', response.data);
      toast.success(`${files.length} file(s) uploaded ✅`);
      setMoReferenceFiles([]);
      setMoRefRefresh(prev => prev + 1); // Trigger refresh in component
    } catch (err) {
      console.error('❌ MO reference upload failed:', err);
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setMoRefUploading(false);
    }
  };

  const handleMoRefDelete = async (projectId, fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await api.delete(`/projects/${projectId}/mo-references/${fileId}`);
      toast.success('File deleted ✅');
      setMoRefRefresh(prev => prev + 1); // Trigger refresh
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const uploadMoReferencesOnProjectCreate = async (projectId) => {
    if (!moReferenceFiles.length) {
      console.log('📤 No MO reference files to upload');
      return;
    }

    try {
      console.log(`📤 Uploading ${moReferenceFiles.length} MO reference file(s) for project ${projectId}`);
      const formData = new FormData();
      moReferenceFiles.forEach((file, idx) => {
        console.log(`  [${idx + 1}] ${file.name} (${file.size} bytes, type: ${file.type})`);
        formData.append('images', file);
      });

      const response = await api.post(`/projects/${projectId}/mo-references`, formData);

      console.log('✅ MO references upload successful:', response.data);
      toast.success(`${moReferenceFiles.length} file(s) uploaded`);
      setMoReferenceFiles([]);
    } catch (err) {
      console.error('❌ MO reference upload failed:', err);
      console.error('  Error details:', err.response?.data || err.message);
      throw err; // Re-throw so outer catch handler can deal with it
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this project?\nProjects with active tasks will return an error.')) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success('Project deleted');
      load();
    } catch(err) {
      toast.error(err.response?.data?.message || 'Delete failed — please check for active tasks');
    }
  };

  const handleRestore = async (id, e) => {
    e.stopPropagation();
    try {
      await api.put(`/projects/${id}`, { status: 'active' });
      toast.success('Project restored! ✅');
      load();
    } catch(err) {
      toast.error(err.response?.data?.message || 'Restore failed');
    }
  };

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="text-gray-500 text-sm">
            {filterClient
              ? `${displayedProjects.length} project${displayedProjects.length !== 1 ? 's' : ''} · ${filterClient}`
              : `${projects.length} projects`
            }
          </p>
        </div>
        {canWrite && <button onClick={openCreate} className="flex items-center justify-center sm:justify-start gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors shadow-sm w-full sm:w-auto">
          <Plus className="w-4 h-4" /> New Project
        </button>}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search projects..." className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
          </div>
        </div>

        {/* Client filter — horizontal scrollable cards */}
        {clients.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
            <button
              onClick={() => setFilterClient('')}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all ${
                !filterClient
                  ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'
              }`}
            >
              All Clients
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${!filterClient ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {projects.length}
              </span>
            </button>
            {[...new Set(clients.map(c => c.name))].sort().map(name => {
              const count = projects.filter(p => (p.client_name||'').toLowerCase() === name.toLowerCase()).length;
              const isActive = filterClient.toLowerCase() === name.toLowerCase();
              return (
                <button
                  key={name}
                  onClick={() => setFilterClient(isActive ? '' : name)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all ${
                    isActive
                      ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isActive ? 'bg-white' : 'bg-amber-400'}`} />
                  {name}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {STATUSES.map(s => (
            <button key={s} onClick={() => { setStatusFilter(s); setReadyFilter('all'); }} className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-medium capitalize transition-colors ${statusFilter === s && readyFilter === 'all' ? 'bg-amber-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-amber-300'}`}>
              {s}{countsLoading
                ? <span className={`ml-1 inline-block w-3 h-3 rounded-full border-2 align-middle animate-spin ${statusFilter === s && readyFilter === 'all' ? 'border-white/40 border-t-white' : 'border-gray-300 border-t-amber-500'}`} />
                : counts[s] !== undefined ? <span className={`ml-1 text-xs font-bold px-1.5 py-0.5 rounded-full ${statusFilter === s && readyFilter === 'all' ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>({counts[s]})</span> : null}
            </button>
          ))}
          <button
            onClick={() => { setStatusFilter('all'); setReadyFilter('no'); }}
            className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-medium transition-colors ${readyFilter === 'no' ? 'bg-orange-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-orange-300'}`}
          >
            ⏳ Not Ready{countsLoading
              ? <span className={`ml-1 inline-block w-3 h-3 rounded-full border-2 align-middle animate-spin ${readyFilter === 'no' ? 'border-white/40 border-t-white' : 'border-gray-300 border-t-amber-500'}`} />
              : counts['not_ready'] !== undefined ? <span className={`ml-1 text-xs font-bold px-1.5 py-0.5 rounded-full ${readyFilter === 'no' ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>({counts['not_ready']})</span> : null}
          </button>
        </div>
        {/* Sort controls */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 font-medium">Sort by:</span>
          <button
            onClick={() => toggleSort('order_date')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
              sortBy.startsWith('order_date') ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'
            }`}
          >
            <Calendar className="w-3 h-3" /> Order Date {sortIcon("order_date")}
          </button>
          <button
            onClick={() => toggleSort('deadline')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
              sortBy.startsWith('deadline') ? 'bg-red-50 border-red-300 text-red-700' : 'bg-white border-gray-200 text-gray-600 hover:border-red-300'
            }`}
          >
            <AlertTriangle className="w-3 h-3" /> Due Date {sortIcon("deadline")}
          </button>
          {sortBy && (
            <button onClick={() => setSortBy('')} className="text-xs text-gray-400 hover:text-gray-600 underline">Clear sort</button>
          )}
        </div>
      </div>

      {/* Cards — same style as client portal */}
      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin w-6 h-6 border-4 border-amber-500 border-t-transparent rounded-full" /></div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16 text-gray-400 bg-white rounded-2xl border border-gray-100">
          <p className="text-lg font-medium">No projects found</p>
          <p className="text-sm mt-1">Create your first project to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayedProjects.map(p => {
            const isDelayed = p.delayed_tasks > 0;
            const isDead = p.status === 'deleted';
            return (
              <div key={p.id}
                onClick={() => navigate(`${basePath}/projects/${p.id}`)}
                className={`bg-white rounded-2xl border shadow-sm hover:shadow-md hover:border-amber-300 cursor-pointer transition-all duration-200 p-5 ${isDead ? 'opacity-50 border-gray-200' : 'border-gray-200'}`}
              >
                {/* Top row */}
                <div className="flex items-start gap-3">
                  {/* Thumbnail */}
                  {p.thumbnail_path ? (
                    p.thumbnail_type === 'raw' ? (
                      <div className="flex-shrink-0 w-12 h-12 bg-red-50 rounded-xl border border-red-200 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-red-500" />
                      </div>
                    ) : (
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden border border-gray-200">
                        <img src={p.thumbnail_path} alt="" className="w-full h-full object-cover" onError={e => { e.target.parentElement.innerHTML = `<div class="w-full h-full bg-amber-100 flex items-center justify-center"><span class="text-amber-700 font-bold text-sm">${p.name.charAt(0)}</span></div>`; }} />
                      </div>
                    )
                  ) : (
                    <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <span className="text-amber-700 font-bold text-sm">{p.name.charAt(0)}</span>
                    </div>
                  )}

                  {/* Name + badges */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {isDelayed && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                      <h3 className="font-bold text-gray-900 text-base leading-tight truncate">{p.name}</h3>
                      {p.project_id && (
                        <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded-md flex-shrink-0">{p.project_id}</span>
                      )}
                    </div>
                    {/* Status + priority + ready badges */}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium capitalize status-${p.status}`}>{p.status}</span>
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium capitalize priority-${p.priority}`}>{p.priority}</span>
                      {p.is_ready
                        ? <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">✅ Ready</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-400">Not Ready</span>
                      }
                      {isDelayed && <span className="text-xs px-2.5 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">⚠ Delayed</span>}
                    </div>
                  </div>

                  {/* Actions + arrow */}
                  <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    {canEdit && <button onClick={e => openEdit(p, e)} className="p-1.5 hover:bg-amber-100 rounded-lg text-amber-600 transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>}
                    {p.status !== 'deleted'
                      ? (canDelete ? <button onClick={e => handleDelete(p.id, e)} className="p-1.5 hover:bg-red-100 rounded-lg text-red-500 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button> : null)
                      : <button onClick={e => handleRestore(p.id, e)} className="p-1.5 hover:bg-green-100 rounded-lg text-green-600 transition-colors" title="Restore"><RotateCcw className="w-4 h-4" /></button>
                    }
                    <div className="w-8 h-8 bg-amber-50 rounded-xl border border-amber-200 flex items-center justify-center ml-1">
                      <Eye className="w-4 h-4 text-amber-600" />
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-100 my-3" />

                {/* Meta row */}
                <div className="flex items-center gap-5 flex-wrap text-xs text-gray-500">
                  {p.client_name && (
                    <span className="flex items-center gap-1.5">👤 <strong className="text-gray-700">{p.client_name}</strong></span>
                  )}
                  {p.order_date && (
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      Order: <strong className="text-gray-700">{formatDate(p.order_date)}</strong>
                    </span>
                  )}
                  {p.deadline && (
                    <span className={`flex items-center gap-1.5 ${isDelayed ? 'text-red-600' : ''}`}>
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Due: <strong>{formatDate(p.deadline)}</strong>
                    </span>
                  )}
                  {p.total_amount > 0 && (
                    <span className="flex items-center gap-1">💰 <strong className="text-gray-700">₹{Number(p.total_amount).toLocaleString()}</strong></span>
                  )}
                  {p.created_by_name && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-100 rounded-full text-blue-700 font-semibold">
                      ✏️ Created by: {p.created_by_name}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:720, display:'flex', flexDirection:'column', maxHeight:'92vh'}}>
            <div className="modal-header">
              <div>
                <h2>{editProject ? 'Edit Project' : 'New Project'}</h2>
              </div>
              <button onClick={() => setShowModal(false)} className="modal-close">✕</button>
            </div>
            <div style={{overflowY:'auto', flex:1}}>
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Project Name *</label>
                  <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="field-input" placeholder="e.g. Bedroom Furniture Set" />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Client Name</label>
                  <ClientSelect
                    clients={clients}
                    value={form.client_name}
                    onChange={v => setForm(f => ({...f, client_name: v}))}
                    onClientSelect={c => setForm(f => ({...f, client_name: c.name, client_phone: c.phone || f.client_phone}))}
                    placeholder="Search or type client name..."
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Client Phone</label>
                  <input value={form.client_phone} onChange={e => setForm({...form, client_phone: e.target.value})} className="field-input" placeholder="Phone number" />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="field-input">
                    {PRIORITIES.map(p => <option key={p} value={p} className="capitalize">{p}</option>)}
                  </select>
                </div>
                {editProject && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="field-input">
                      {['active','inactive','completed','on_hold','cancelled','deleted'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Order Date</label>
                  <input type="date" value={form.order_date} onChange={e => setForm({...form, order_date: e.target.value})} className="field-input" />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Deadline</label>
                  <input type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} className="field-input" />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Total Amount ({formatCurrency ? formatCurrency(0).replace("0","") : "₹"})</label>
                  <input type="number" value={form.total_amount} onChange={e => setForm({...form, total_amount: e.target.value})} className="field-input" placeholder="0" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} className="w-full border border-gray-200 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none" placeholder="Project description..." />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="w-full border border-gray-200 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none" placeholder="Additional notes..." />
                </div>

                {/* Is Ready Toggle */}
                <div className="md:col-span-2">
                  <button
                    type="button"
                    onClick={() => {
        const nowReady = !form.is_ready;
        setForm({ ...form, is_ready: nowReady, status: nowReady ? 'active' : 'inactive' });
      }}
                    className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl border-2 transition-all ${
                      form.is_ready
                        ? 'border-green-400 bg-green-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    {form.is_ready
                      ? <ToggleRight className="w-7 h-7 text-green-600 flex-shrink-0" />
                      : <ToggleLeft className="w-7 h-7 text-gray-400 flex-shrink-0" />
                    }
                    <div className="text-left">
                      <p className={`text-sm font-semibold ${form.is_ready ? 'text-green-700' : 'text-gray-600'}`}>
                        {form.is_ready ? '✅ Project Ready to Start' : 'Project Not Ready Yet'}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {form.is_ready
                          ? 'This project will remain active and tasks can be assigned'
                          : 'This project will stay inactive until marked as ready'}
                      </p>
                    </div>
                  </button>
                </div>

                {/* MO References Section - Only shown in edit mode */}
                {editProject && (
                  <div className="md:col-span-2">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">MO Reference Images & PDFs</label>
                    <MoReferencesSection 
                      projectId={editProject.id} 
                      refreshKey={moRefRefresh}
                      onUpload={(files) => handleMoRefUpload(files, editProject.id)}
                      onDelete={(fileId) => handleMoRefDelete(editProject.id, fileId)}
                      uploading={moRefUploading}
                    />
                  </div>
                )}

                {/* Simple file input - Only shown in create mode for initial upload */}
                {!editProject && (
                  <div className="md:col-span-2">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">MO Reference Images (Admin) - Upload after creating project</label>
                    <input type="file" multiple accept="image/*,.pdf" onChange={e => setMoReferenceFiles(Array.from(e.target.files || []))} className="w-full text-xs text-gray-600" />
                    {moReferenceFiles.length > 0 && (
                      <div className="mt-2 text-xs text-gray-600">
                        {moReferenceFiles.length} file(s) selected - will upload after project is created
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
                <button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-2 sm:py-2.5 rounded-xl font-medium text-sm transition-colors">
                  {editProject ? 'Update Project' : 'Create Project'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-gray-200 text-gray-600 hover:bg-gray-50 py-2 sm:py-2.5 rounded-xl font-medium text-sm transition-colors">
                  Cancel
                </button>
              </div>
            </form>
            </div>
          </div>
            </div>
        </div>
      )}
    </div>
  
    </PermissionGuard>
  );
}
